import React from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Shop } from '@shared/components/ui/shop';
import { Button } from '@shared/components/ui/button';
import { Skeleton } from '@shared/components/ui/shadcn/skeleton';
import { Customer, PaginationMeta } from '../../models';
import { SortDirection } from '../../models/CustomerFilters';
import { CustomerMobileCard } from './CustomerMobileCard';
import { customerOriginHelpers } from '../../constants';
import { getOrderTypeLabel } from '../../utils/orderTypeHelpers';
import { resolveCustomerPaginationState } from '../../utils/paginationMeta';

interface CustomerTableProps {
  customers?: Customer[];
  paginationMeta?: PaginationMeta;
  isLoading: boolean;
  isFetching?: boolean;
  onPaginationChange: (page: number, perPage?: number) => void;
  onSortChange: (column: string, direction: SortDirection) => void;
  currentSort: { column: string; direction: SortDirection };
}

const TABLE_HEADERS = ['client', 'email', 'firstEntry', 'saleType', 'source'] as const;

export const CustomerTable = ({
  customers,
  paginationMeta,
  isLoading,
  isFetching = false,
  onPaginationChange,
}: CustomerTableProps) => {
  const { t } = useTranslation(['marketingModule', 'createEditClientsModule']);

  const showInitialSkeleton = isLoading && !customers?.length;

  const renderPagination = () => {
    const pagination = resolveCustomerPaginationState(paginationMeta, customers?.length ?? 0, isFetching);

    if (!pagination) return null;

    return (
      <div className="flex flex-wrap items-center justify-between gap-2 pt-2">
        <p className="text-xs text-muted-foreground">
          {t('customerTable.showing', 'Mostrando {{from}} - {{to}} de {{total}} clientes', {
            from: pagination.from,
            to: pagination.to,
            total: pagination.total,
          })}
        </p>
        {pagination.hasMultiplePages && (
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground">
              {t('customerTable.pagination.page', 'Página {{current}} de {{total}}', {
                current: pagination.currentPage,
                total: pagination.lastPage,
              })}
            </span>
            <Button
              type="button"
              variant="outline"
              size="icon"
              className="h-8 w-8 border-border"
              disabled={!pagination.canGoPrev}
              onClick={() => onPaginationChange(pagination.currentPage - 1)}
              data-testid="marketing-customers-prev"
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button
              type="button"
              variant="outline"
              size="icon"
              className="h-8 w-8 border-border"
              disabled={!pagination.canGoNext}
              onClick={() => onPaginationChange(pagination.currentPage + 1)}
              data-testid="marketing-customers-next"
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        )}
      </div>
    );
  };

  const headerLabels: Record<(typeof TABLE_HEADERS)[number], string> = {
    client: t('customerTable.headers.client', 'Cliente'),
    email: t('customerTable.headers.email', 'Correo'),
    firstEntry: t('customerTable.headers.firstEntry', '1ª entrada'),
    saleType: t('customerTable.headers.saleType', 'Tipo venta'),
    source: t('customerTable.headers.source', 'Fuente'),
  };

  if (showInitialSkeleton) {
    return (
      <div className="space-y-3">
        <div className="lg:hidden space-y-2">
          {Array.from({ length: 3 }).map((_, index) => (
            <div key={`mobile-skeleton-${index}`} className="p-3 rounded-lg border border-border space-y-2">
              <Skeleton className="h-4 w-32" />
              <Skeleton className="h-3 w-48" />
            </div>
          ))}
        </div>
        <div className="hidden lg:block rounded-lg border border-border overflow-hidden">
          <div className="border-b border-border bg-card-bg px-3 py-2.5">
            <Skeleton className="h-4 w-full max-w-md" />
          </div>
          {Array.from({ length: 8 }).map((_, index) => (
            <div
              key={`row-skeleton-${index}`}
              className="flex gap-4 px-3 py-2.5 border-b border-border/50 last:border-b-0"
            >
              <Skeleton className="h-4 w-28" />
              <Skeleton className="h-4 w-36" />
              <Skeleton className="h-4 w-20" />
              <Skeleton className="h-4 w-16" />
              <Skeleton className="h-4 w-16" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {/* Mobile */}
      <div className="lg:hidden space-y-2">
        {customers?.map((customer) => (
          <CustomerMobileCard key={customer.id} customer={customer} />
        ))}
        {customers?.length === 0 && (
          <p className="text-center py-6 text-sm text-muted-foreground">
            {t('customerTable.empty', 'No se encontraron clientes')}
          </p>
        )}
        {renderPagination()}
      </div>

      {/* Desktop */}
      <div className="hidden lg:block">
        <div className="rounded-lg border border-border overflow-hidden">
          <div className="max-h-[min(520px,60vh)] overflow-auto">
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr className="border-b border-border bg-card-bg">
                  {TABLE_HEADERS.map((key) => (
                    <th
                      key={key}
                      className="sticky top-0 z-10 h-10 border-b border-border bg-card-bg px-3 text-left text-xs font-medium text-muted-foreground"
                    >
                      {headerLabels[key]}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {customers?.map((customer) => (
                  <tr key={customer.id} className="border-b border-border/50 transition-colors hover:bg-muted/20">
                    <td className="px-3 py-2 align-middle">
                      <Shop name={customer.name} src={customer.logo_hash} className="py-0" />
                    </td>
                    <td className="max-w-[220px] truncate px-3 py-2 align-middle text-sm text-muted-foreground">
                      {customer.email_admin}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 align-middle text-sm tabular-nums">
                      {customer.first_entry_at ? customer.first_entry_at.split('-').reverse().join('/') : '-'}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 align-middle text-sm">
                      {getOrderTypeLabel(customer.order_type)}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 align-middle text-sm">
                      {customerOriginHelpers.getSourceLabel(customer.customer_origin, t)}
                    </td>
                  </tr>
                ))}
                {customers?.length === 0 && (
                  <tr>
                    <td colSpan={5} className="px-3 py-8 text-center text-sm text-muted-foreground">
                      {t('customerTable.empty', 'No se encontraron clientes')}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
        {renderPagination()}
      </div>
    </div>
  );
};
